import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import type {
  BroadcastEstimateResponse,
  BroadcastResponse,
  BroadcastRecipientResponse,
  BroadcastSegment,
  BroadcastChannel,
} from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { PageHeader } from '../../components/PageHeader';
import { useLocalisedName } from '../../i18n/localisedName';
import { useBranchPicker } from '../scheduling/useBranchPicker';
import { money } from '../catalog/money';

const SEGMENTS: readonly BroadcastSegment[] = ['ALL', 'ACTIVE_PACKAGE', 'INACTIVE_90_DAYS'];
const CHANNELS: readonly BroadcastChannel[] = ['WHATSAPP', 'PUSH', 'SMS'];

/**
 * The broadcast composer (FR-1005, DEC-045, RULE-002).
 *
 * The screen is built around one idea: <strong>you are told what this will do before you do it.</strong>
 * The estimate runs the same segment resolution and the same opt-in filter the send will, so the
 * number beside the button is the number of messages that go out — and the count of people it will
 * <em>not</em> reach is shown just as prominently, because "412 of 1,340" tells a clinic something
 * true about its own consent rate that a single figure would hide.
 *
 * There is no way to send to somebody who has not opted in. Not a warning, not a checkbox: the
 * server refuses it twice over, and this screen has no control that would express the attempt.
 */
export function BroadcastsPage() {
  const { t } = useTranslation();
  const { name } = useLocalisedName();
  const { clinicId, branches } = useBranchPicker();

  const [segment, setSegment] = useState<BroadcastSegment>('INACTIVE_90_DAYS');
  const [channel, setChannel] = useState<BroadcastChannel>('WHATSAPP');
  const [scope, setScope] = useState<string>('');
  const [bodyAr, setBodyAr] = useState('');
  const [bodyEn, setBodyEn] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [reportFor, setReportFor] = useState<string | null>(null);

  const filter = `${segment}:${channel}:${scope}`;
  const estimate = useApiQuery<BroadcastEstimateResponse>(
    queryKeys.broadcastEstimate(clinicId ?? '', filter),
    `/clinics/${clinicId}/broadcasts/estimate?segment=${segment}&channel=${channel}` +
      (scope ? `&branchId=${scope}` : ''),
    { enabled: Boolean(clinicId) },
  );

  const history = useApiQuery<BroadcastResponse[]>(
    queryKeys.broadcasts(clinicId ?? ''),
    `/clinics/${clinicId}/broadcasts`,
    { enabled: Boolean(clinicId) },
  );

  const recipients = useApiQuery<BroadcastRecipientResponse[]>(
    queryKeys.broadcastRecipients(clinicId ?? '', reportFor ?? ''),
    `/clinics/${clinicId}/broadcasts/${reportFor}/recipients`,
    { enabled: Boolean(clinicId && reportFor) },
  );

  const send = useApiMutation<BroadcastResponse, void>(
    () => ({ path: `/clinics/${clinicId}/broadcasts`, options: { method: 'POST', body: composed() } }),
    {
      invalidates: [queryKeys.broadcasts(clinicId ?? '')],
      onSuccess: () => {
        setConfirming(false);
        setBodyAr('');
        setBodyEn('');
      },
    },
  );

  // Composing and sending are two calls; the screen makes them one action, because a draft nobody
  // ever sends is not a state a clinic administrator asked for. The API keeps them apart because a
  // scheduled send has to exist between the two.
  const composed = () => ({
    segment,
    channel,
    branchId: scope || undefined,
    bodyAr: bodyAr.trim() || undefined,
    bodyEn: bodyEn.trim() || undefined,
  });

  const dispatch = useApiMutation<BroadcastResponse, string>(
    (broadcastId) => ({
      path: `/clinics/${clinicId}/broadcasts/${broadcastId}/send`,
      options: { method: 'POST', body: {} },
    }),
    { invalidates: [queryKeys.broadcasts(clinicId ?? '')] },
  );

  const written = bodyAr.trim().length > 0 || bodyEn.trim().length > 0;
  const reach = estimate.data?.reachable ?? 0;

  if (!clinicId) {
    return <PageHeader title={t('nav.items.broadcasts')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader title={t('nav.items.broadcasts')} description={t('broadcasts.description')} />

      <section className="panel">
        <h2 className="panel__heading">{t('broadcasts.compose')}</h2>

        <div className="filters">
          <label className="field">
            <span>{t('broadcasts.segment')}</span>
            <select value={segment} onChange={(event) => setSegment(event.target.value as BroadcastSegment)}>
              {SEGMENTS.map((option) => (
                <option key={option} value={option}>
                  {t(`broadcasts.segments.${option}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>{t('broadcasts.channel')}</span>
            <select value={channel} onChange={(event) => setChannel(event.target.value as BroadcastChannel)}>
              {CHANNELS.map((option) => (
                <option key={option} value={option}>
                  {t(`broadcasts.channels.${option}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>{t('broadcasts.scope')}</span>
            <select value={scope} onChange={(event) => setScope(event.target.value)}>
              <option value="">{t('broadcasts.wholeClinic')}</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {name(branch, branch.code)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="stack">
          <label className="field">
            <span>{t('broadcasts.bodyAr')}</span>
            <textarea value={bodyAr} onChange={(event) => setBodyAr(event.target.value)} rows={3} maxLength={2000} dir="rtl" />
          </label>
          <label className="field">
            <span>{t('broadcasts.bodyEn')}</span>
            <textarea value={bodyEn} onChange={(event) => setBodyEn(event.target.value)} rows={3} maxLength={2000} />
          </label>
        </div>

        <AsyncSection query={estimate}>
          {(figures) => (
            <dl className="broadcast-estimate">
              <div>
                <dt>{t('broadcasts.inSegment')}</dt>
                <dd>{figures.inSegment}</dd>
              </div>
              <div>
                <dt>{t('broadcasts.reachable')}</dt>
                <dd>{figures.reachable}</dd>
              </div>
              <div>
                {/* Shown, never hidden: this is what RULE-002 costs the clinic, and it is the
                    number that makes the case for asking patients to opt in. */}
                <dt>{t('broadcasts.optedOut')}</dt>
                <dd>{figures.optedOut}</dd>
              </div>
              <div>
                <dt>{t('broadcasts.unreachable')}</dt>
                <dd>{figures.unreachable}</dd>
              </div>
              <div>
                <dt>{t('broadcasts.cost')}</dt>
                <dd>
                  {money(figures.cost)}
                </dd>
              </div>
            </dl>
          )}
        </AsyncSection>
        <p className="muted">{t('broadcasts.costCaveat')}</p>

        {!confirming ? (
          <button
            type="button"
            className="button"
            disabled={!written || reach === 0}
            onClick={() => setConfirming(true)}
          >
            {t('broadcasts.review')}
          </button>
        ) : (
          <div className="notice">
            <p>{t('broadcasts.confirm', { people: reach })}</p>
            <div className="stack stack--row">
              <button
                type="button"
                className="button"
                disabled={send.isPending || dispatch.isPending}
                onClick={() => {
                  send.mutate(undefined, {
                    onSuccess: (created) => {
                      if (created.id) {
                        dispatch.mutate(created.id);
                      }
                    },
                  });
                }}
              >
                {t('broadcasts.send')}
              </button>
              <button type="button" className="button button--ghost button--compact" onClick={() => setConfirming(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="panel">
        <h2 className="panel__heading">{t('broadcasts.history')}</h2>
        <AsyncSection
          query={history}
          isEmpty={(rows) => rows.length === 0}
          emptyTitle={t('broadcasts.noneYet')}
        >
          {(rows) => (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('broadcasts.sentAt')}</th>
                    <th>{t('broadcasts.segment')}</th>
                    <th>{t('broadcasts.channel')}</th>
                    <th>{t('broadcasts.reached')}</th>
                    <th>{t('broadcasts.suppressed')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.sentAt ?? row.scheduledFor ?? '—'}</td>
                      <td>{t(`broadcasts.segments.${row.segment}`)}</td>
                      <td>{t(`broadcasts.channels.${row.channel}`)}</td>
                      <td>{row.queuedCount}</td>
                      <td>{row.suppressedCount}</td>
                      <td>
                        <button
                          type="button"
                          className="button button--ghost button--compact"
                          onClick={() => setReportFor(row.id ?? null)}
                        >
                          {t('broadcasts.report')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AsyncSection>
      </section>

      {reportFor ? (
        <section className="panel">
          <h2 className="panel__heading">{t('broadcasts.reportTitle')}</h2>
          <AsyncSection query={recipients} isEmpty={(rows) => rows.length === 0}>
            {(rows) => (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('broadcasts.patient')}</th>
                      <th>{t('broadcasts.outcome')}</th>
                      <th>{t('broadcasts.why')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.patientMembershipId}>
                        <td>{row.patientName}</td>
                        <td>{t(`broadcasts.outcomes.${row.outcome}`)}</td>
                        <td className="muted">{row.suppressedReason ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </AsyncSection>
        </section>
      ) : null}
    </>
  );
}
